import { ChangeDetectionStrategy, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';

import { UntilDestroy } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';

import { Mapper, MarkFormAsTouchedIfInvalid } from 'tiime-expert-utils';
import { DialogBase } from 'tiime-material';

import { DocumentApiService } from '@api-services/document-api.service';
import { QUERY_PARAMS } from '@constants/query-params.constant';
import { DocumentCategoryIdentifier } from '@enums/document-category-identifier.enum';
import { DocumentCategory } from '@models/document-category';
import { NavigationService } from '@modules/core/navigation/navigation.service';
import { UploadDocumentOverlayService } from '@modules/shared/overlays/upload-file-overlay/upload-document-overlay/upload-document-overlay.service';
import { UploadDocumentService } from '@modules/shared/overlays/upload-file-overlay/upload-document-overlay/upload-document.service';
import { UPLOAD_FILE_CONFIG } from '@modules/shared/overlays/upload-file-overlay/upload-file-config.constant';

import { UploadDocumentDialogData } from './upload-document-dialog-data';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-upload-document-dialog',
  templateUrl: './upload-document-dialog.component.html',
  styleUrls: ['./upload-document-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UploadDocumentDialogComponent extends DialogBase<UploadDocumentDialogComponent> implements OnInit {
  @ViewChild('fileInput', { read: ElementRef }) fileInput: ElementRef<HTMLElement>;

  documentCategories$: Observable<DocumentCategory[]>;
  documentCategoryFilter = new FormControl<number | null>(null, Validators.required);

  readonly acceptedTypes = UPLOAD_FILE_CONFIG.extendedAcceptedFileTypes.join();
  readonly hasSelectedDocuments: boolean = false;
  readonly selectedDocuments: FileList | null = null;

  private documentCategoryCreated = false;

  get documentCategoryErrorMessage(): string | null {
    return this.documentCategoryFilter.touched && this.documentCategoryFilter.hasError('required')
      ? 'Espace requis'
      : null;
  }

  readonly mapToAvailableDocumentCategories: Mapper<DocumentCategory[], DocumentCategory[]> = (
    documentCategories: DocumentCategory[]
  ) =>
    documentCategories?.filter(
      (documentCategory: DocumentCategory) => documentCategory.identifier !== DocumentCategoryIdentifier.expenseReport
    );

  constructor(
    dialogRef: MatDialogRef<UploadDocumentDialogComponent>,
    private readonly uploadDocumentOverlayService: UploadDocumentOverlayService,
    private readonly navigationService: NavigationService,
    private readonly route: ActivatedRoute,
    private readonly documentApiService: DocumentApiService,
    private readonly uploadDocumentService: UploadDocumentService,
    @Inject(MAT_DIALOG_DATA) private readonly data: UploadDocumentDialogData
  ) {
    super(dialogRef);
    this.hasSelectedDocuments = data.selectedDocuments?.length > 0;
    this.selectedDocuments = data.selectedDocuments;
  }

  ngOnInit(): void {
    this.initDocumentCategories();
    this.initDocumentCategoryFilter();
  }

  createdDocumentCategory(documentCategory: DocumentCategory): void {
    this.documentCategoryCreated = true;
    this.initDocumentCategories();
    this.documentCategoryFilter.setValue(documentCategory.id);
  }

  @MarkFormAsTouchedIfInvalid('documentCategoryFilter')
  uploadFiles(): void {
    if (this.hasSelectedDocuments) {
      return this.onFileSelected(this.data.selectedDocuments);
    }
    this.fileInput?.nativeElement.click();
  }

  onFileSelected(files: FileList): void {
    if (files.length === 0) {
      return;
    }

    this.updateDocumentsQueryParams();
    this.uploadDocumentService.selectedDocumentCategoryId = this.documentCategoryFilter.value;
    this.uploadDocumentOverlayService.openOverlayWithFiles(files);
    this.close(this.documentCategoryCreated);
  }

  override cancel(): void {
    this.dialogRef.close(this.documentCategoryCreated);
  }

  private initDocumentCategories(): void {
    this.documentCategories$ = this.documentApiService.getDocumentCategories();
  }

  private initDocumentCategoryFilter(): void {
    if (!this.data.selectedDocumentCategory) {
      return;
    }

    this.documentCategories$
      .pipe(
        take(1),
        tap(documentCategories => {
          const foundedDocumentCategory = documentCategories.find(
            documentCategory =>
              documentCategory.id === this.data.selectedDocumentCategory.id &&
              documentCategory.identifier !== DocumentCategoryIdentifier.expenseReport
          );
          if (foundedDocumentCategory) {
            this.documentCategoryFilter.setValue(foundedDocumentCategory.id);
          }
        })
      )
      .subscribe();
  }

  private updateDocumentsQueryParams(): void {
    this.navigationService
      .isCompanyDocumentsRouteActive()
      .pipe(
        filter((active: boolean) => active),
        tap(() =>
          this.navigationService.updateQueryParams(
            {
              [QUERY_PARAMS.documentCategory]: this.documentCategoryFilter.value
            },
            this.route
          )
        )
      )
      .subscribe();
  }
}
